import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  MenuItem,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Save,
  Cancel,
  AcUnit,
  Favorite,
  Celebration,
  Flag,
  Star,
  VolunteerActivism,
  LocalFlorist
} from '@mui/icons-material';
import { eventService } from '../../services/eventService';

// Icon mapping for selection
const iconOptions = [
  { value: 'Star', label: 'Star (Default)', icon: <Star /> },
  { value: 'AcUnit', label: 'Snowflake (Christmas)', icon: <AcUnit /> },
  { value: 'Favorite', label: 'Heart (Valentines)', icon: <Favorite /> },
  { value: 'Celebration', label: 'Confetti (New Year)', icon: <Celebration /> },
  { value: 'Flag', label: 'Flag (Independence)', icon: <Flag /> },
  { value: 'VolunteerActivism', label: 'Hand Heart', icon: <VolunteerActivism /> },
  { value: 'LocalFlorist', label: 'Flower', icon: <LocalFlorist /> }
];

const ManageEventsDialog = ({ open, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    message: '',
    theme_primary: '#1976d2',
    theme_secondary: '#dc004e',
    text_color: '#ffffff',
    icon_name: 'Star',
    event_date: ''
  });

  useEffect(() => {
    if (open) {
      loadEvents();
    }
  }, [open]);

  const loadEvents = async () => {
    setLoading(true);
    const data = await eventService.getAllEvents();
    setEvents(data);
    setLoading(false);
  };

  const handleEdit = (event) => {
    setCurrentEvent(event);
    setFormData({
      name: event.name,
      title: event.title,
      message: event.message,
      theme_primary: event.theme_primary,
      theme_secondary: event.theme_secondary,
      text_color: event.text_color,
      icon_name: event.icon_name,
      event_date: event.event_date || ''
    });
    setEditMode(true);
  };

  const handleAdd = () => {
    setCurrentEvent(null);
    setFormData({
      name: '',
      title: '',
      message: '',
      theme_primary: '#1976d2',
      theme_secondary: '#dc004e',
      text_color: '#ffffff',
      icon_name: 'Star',
      event_date: ''
    });
    setEditMode(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      await eventService.deleteEvent(id);
      loadEvents();
    }
  };

  const handleToggleActive = async (event) => {
    if (event.is_active) {
      // Deactivate
      await eventService.deactivateAllEvents();
    } else {
      // Activate
      await eventService.activateEvent(event.id);
    }
    loadEvents();
  };

  const handleGenerate = () => {
    if (!formData.name) return;
    
    const lowerName = formData.name.toLowerCase();
    let generatedData = {};

    if (lowerName.includes('christmas') || lowerName.includes('xmas') || lowerName.includes('pasko')) {
      generatedData = {
        title: 'Merry Christmas!',
        message: 'Wishing you a season filled with warmth, comfort, and good cheer! From all of us at LGU Concepcion.',
        theme_primary: '#d32f2f',
        theme_secondary: '#1b5e20',
        text_color: '#ffffff',
        icon_name: 'AcUnit',
        event_date: `${new Date().getFullYear()}-12-25`
      };
    } else if (lowerName.includes('new year') || lowerName.includes('bagong taon')) {
      generatedData = {
        title: 'Happy New Year!',
        message: 'Wishing you a prosperous and successful year ahead! May this year bring new happiness and goals.',
        theme_primary: '#1a237e',
        theme_secondary: '#ffca28',
        text_color: '#ffffff',
        icon_name: 'Celebration',
        event_date: `${new Date().getFullYear() + 1}-01-01`
      };
    } else if (lowerName.includes('valentine') || lowerName.includes('love') || lowerName.includes('heart')) {
      generatedData = {
        title: 'Happy Valentine\'s Day!',
        message: 'Celebrating love, friendship, and community service. Wishing you a lovely day!',
        theme_primary: '#e91e63',
        theme_secondary: '#f8bbd0',
        text_color: '#ffffff',
        icon_name: 'Favorite',
        event_date: `${new Date().getFullYear()}-02-14`
      };
    } else if (lowerName.includes('independence') || lowerName.includes('freedom') || lowerName.includes('kalayaan')) {
      generatedData = {
        title: 'Happy Independence Day!',
        message: 'Celebrating our freedom and nationhood. Mabuhay ang Pilipinas!',
        theme_primary: '#0d47a1',
        theme_secondary: '#b71c1c',
        text_color: '#ffffff',
        icon_name: 'Flag',
        event_date: `${new Date().getFullYear()}-06-12`
      };
    } else if (lowerName.includes('halloween') || lowerName.includes('scary')) {
      generatedData = {
        title: 'Happy Halloween!',
        message: 'Have a spooktacular day! Stay safe and enjoy the treats.',
        theme_primary: '#e65100',
        theme_secondary: '#212121',
        text_color: '#ffffff',
        icon_name: 'Star',
        event_date: `${new Date().getFullYear()}-10-31`
      };
    } else if (lowerName.includes('easter') || lowerName.includes('resurrection')) {
      generatedData = {
        title: 'Happy Easter!',
        message: 'Wishing you a blessed and joyful Easter Sunday!',
        theme_primary: '#9c27b0',
        theme_secondary: '#ce93d8',
        text_color: '#ffffff',
        icon_name: 'LocalFlorist',
        event_date: '' // Dynamic date
      };
    } else if (lowerName.includes('fiesta') || lowerName.includes('festival')) {
      generatedData = {
        title: 'Happy Fiesta!',
        message: 'Join us in celebrating our town fiesta! Viva!',
        theme_primary: '#ff6f00',
        theme_secondary: '#ffd54f',
        text_color: '#ffffff',
        icon_name: 'Celebration',
        event_date: '' // Variable
      };
    } else {
      // Generic default
      generatedData = {
        title: `Happy ${formData.name}!`,
        message: `Wishing everyone a wonderful ${formData.name}.`,
        theme_primary: '#1976d2',
        theme_secondary: '#dc004e',
        text_color: '#ffffff',
        icon_name: 'Star',
        event_date: ''
      };
    }

    setFormData({
      ...formData,
      ...generatedData
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.title || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    if (currentEvent) {
      // Update
      await eventService.updateEvent(currentEvent.id, formData);
    } else {
      // Create
      await eventService.createEvent(formData);
    }
    
    setEditMode(false);
    loadEvents();
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setCurrentEvent(null);
    setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editMode ? (currentEvent ? 'Edit Event' : 'Add New Event') : 'Manage System Events'}
      </DialogTitle>
      <DialogContent>
        {editMode ? (
          <Box component="form" sx={{ mt: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Event Name (Internal)"
                  fullWidth
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
                <Button 
                  variant="contained" 
                  onClick={handleGenerate}
                  disabled={!formData.name}
                  sx={{ minWidth: '120px' }}
                >
                  Generate
                </Button>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Event Date (Month & Day)"
                  type="date"
                  fullWidth
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="The year is ignored. This event will automatically repeat annually on this date."
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Greeting Title"
                  fullWidth
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Greeting Message"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Primary Color"
                  fullWidth
                  type="color"
                  value={formData.theme_primary}
                  onChange={(e) => setFormData({ ...formData, theme_primary: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Secondary Color"
                  fullWidth
                  type="color"
                  value={formData.theme_secondary}
                  onChange={(e) => setFormData({ ...formData, theme_secondary: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Text Color"
                  fullWidth
                  type="color"
                  value={formData.text_color}
                  onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Icon"
                  fullWidth
                  value={formData.icon_name}
                  onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                >
                  {iconOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {option.icon}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              {/* Preview Box */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
                <Box sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  background: `linear-gradient(135deg, ${formData.theme_primary} 0%, ${formData.theme_secondary} 100%)`,
                  color: formData.text_color,
                  textAlign: 'center'
                }}>
                  <Typography variant="h5" fontWeight="bold">{formData.title || 'Title'}</Typography>
                  <Typography variant="body1">{formData.message || 'Message content...'}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button startIcon={<Add />} variant="contained" onClick={handleAdd}>
                Add New Event
              </Button>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List>
                {events.map((event) => (
                  <React.Fragment key={event.id}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">{event.name}</Typography>
                            {event.is_active && (
                              <Typography variant="caption" sx={{ bgcolor: 'success.main', color: 'white', px: 1, borderRadius: 1 }}>
                                ACTIVE
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={`${event.title} - ${event.message.substring(0, 50)}...`}
                      />
                      <ListItemSecondaryAction>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={event.is_active}
                              onChange={() => handleToggleActive(event)}
                              color="primary"
                            />
                          }
                          label={event.is_active ? "On" : "Off"}
                          labelPlacement="start"
                        />
                        <IconButton onClick={() => handleEdit(event)} color="primary">
                          <Edit />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(event.id)} color="error">
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
                {events.length === 0 && (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No events found. Create one to get started.
                  </Typography>
                )}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {editMode ? (
          <>
            <Button onClick={handleCancelEdit} startIcon={<Cancel />}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" startIcon={<Save />}>Save</Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManageEventsDialog;
